import { useMemo } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useStreamFunctions } from 'stream/globalFunction/useStreamFunctions';
import { z } from 'zod';

import { validatePythonVariable } from '@polyai/common/lib/stringHelper';
import { FunctionNameSchema } from '@polyai/schemas';
import {
  flowFunctionsSelectors,
  selectSteps,
} from '@polyai/stream/store/reducers/flows';
import {
  isAdvancedStep,
  isDefaultStep,
  isFunctionStep,
} from '@polyai/stream/store/reducers/flows/types';
import { Stack } from '@polyai/ui/components/atoms/Stack';
import Text from '@polyai/ui/components/atoms/Text';
import { ConfirmationModal } from '@polyai/ui/components/molecules/ConfirmationModal';

import {
  type IconBadgeType,
  NodeBase,
} from 'screens/FlowDetails/components/NodeBase';
import useFlowState from 'screens/FlowDetails/hooks/useFlowState';
import { SelectOptionSchemaTransformed } from 'utils/validators/shared';
import FormProvider, { FormSelect, FormTextInput } from '../../Form';
import { CREATE_TRANSITION_BUTTON } from './TransitionModalForm.uiCopy';

const Schema = z.object({
  transitionName: FunctionNameSchema,
  toStepName: SelectOptionSchemaTransformed.optional(),
});

type SchemaProps = z.input<typeof Schema>;

type FormProps = z.output<typeof Schema>;

export type TransitionModalFormValues = FormProps;

interface Props {
  open: boolean;
  onClose: () => void;
  defaultValues?: FormProps;
  onSubmit: (data: FormProps) => void;
  buttonText?: string;
  input?: {
    label?: string;
    placeholder?: string;
  };
  withStepSelect?: boolean;
}

const transformDefaultValues = (data: FormProps) => {
  return {
    transitionName: data.transitionName,
    toStepName: data?.toStepName
      ? {
          value: data.toStepName,
          label: data.toStepName,
        }
      : undefined,
  };
};

const getDefaultValues = (data: FormProps | undefined) => {
  if (!data) {
    return {
      transitionName: '',
      toStepName: undefined,
    };
  }
  return transformDefaultValues(data);
};

const TransitionModalForm = ({
  open,
  onClose,
  defaultValues,
  onSubmit: onSubmitCallback,
  buttonText,
  input,
  withStepSelect,
}: Props) => {
  const { activeFunctions } = useStreamFunctions();
  const { flow } = useFlowState();
  const activeFlowFunctions = useMemo(() => {
    return flow?.transitionFunctions
      ? flowFunctionsSelectors
          .selectAll(flow.transitionFunctions)
          .filter(func => !func.archived)
      : [];
  }, [flow]);

  const stepOptions = useMemo(() => {
    const allSteps = selectSteps(flow);

    return allSteps.map(step => {
      let badgeType: IconBadgeType | undefined;
      if (isDefaultStep(step)) {
        badgeType = 'default_step';
      } else if (isAdvancedStep(step)) {
        badgeType = 'advanced_step';
      } else if (isFunctionStep(step)) {
        badgeType = 'function_step';
      }

      return {
        value: step.name,
        label: badgeType ? (
          <div className="flex items-center">
            <NodeBase.IconBadge className="mr-xs2" size="xs" type={badgeType} />
            <Text as="span">{step.name}</Text>
          </div>
        ) : (
          step.name
        ),
      };
    });
  }, [flow]);

  const RefinedSchema = Schema.superRefine((data, ctx) => {
    const error = validatePythonVariable(data.transitionName);
    if (error !== true) {
      ctx.addIssue({
        path: ['transitionName'],
        code: 'custom',
        message: error,
      });
    }

    // function names in same flow cannot be called the same name
    const globalAndSameFlowFunctionNames = activeFunctions
      .map(f => f.name)
      .concat(activeFlowFunctions?.map(f => f.name) ?? []);

    const isNameInUse = globalAndSameFlowFunctionNames.includes(
      data.transitionName,
    );
    if (isNameInUse) {
      ctx.addIssue({
        path: ['transitionName'],
        code: 'custom',
        message: `${data.transitionName} is already in use`,
      });
    }
  });

  const methods = useForm<SchemaProps, unknown, FormProps>({
    resolver: zodResolver(RefinedSchema),
    defaultValues: getDefaultValues(defaultValues),
    mode: 'onSubmit',
    shouldFocusError: false,
  });
  const {
    watch,
    formState: { isSubmitting },
    setValue,
  } = methods;

  const transitionName = watch('transitionName');

  const onSubmit: SubmitHandler<FormProps> = data => {
    onSubmitCallback(data);
  };

  const contentToRender = (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Stack direction="column" spacing="sm3">
        <FormTextInput<SchemaProps>
          controlKey="transitionName"
          data-test-id="transition-function-name-input"
          label={CREATE_TRANSITION_BUTTON.form.input.label}
          placeholder={CREATE_TRANSITION_BUTTON.form.input.placeholder}
          fullWidth
          {...input}
        />
        {withStepSelect && (
          <FormSelect<SchemaProps>
            controlKey="toStepName"
            data-test-id="transition-function-step-select"
            label={CREATE_TRANSITION_BUTTON.form.select.label}
            options={stepOptions}
            placeholder={CREATE_TRANSITION_BUTTON.form.select.placeholder}
            fullWidth
            onChange={e => {
              setValue('toStepName', {
                label: e.value,
                value: e.value,
              });
            }}
          />
        )}
      </Stack>
    </FormProvider>
  );

  return (
    <ConfirmationModal
      buttonText={buttonText ?? CREATE_TRANSITION_BUTTON.modal.confirm}
      confirmDisabled={!transitionName}
      confirmRequesting={isSubmitting}
      content={contentToRender}
      open={open}
      size="md"
      title={CREATE_TRANSITION_BUTTON.modal.title}
      skipCloseAndConfirm
      onCancelButtonClick={onClose}
      onClose={onClose}
      onConfirmButtonClick={methods.handleSubmit(onSubmit)}
    />
  );
};

export default TransitionModalForm;
